/*
 * PostHog storefront analytics — pageviews plus the homepage → machine PDP
 * funnel. Rendered by snippets/posthog-analytics.liquid, which decides
 * server-side whether this file loads at all (enabled + token, never in the
 * theme editor, production live theme only unless Test mode) and emits the
 * non-secret page context on window.__cumulusPosthogConfig:
 *   {
 *     token: <string>,            // public project token (phc_…)
 *     apiHost: <string>,          // https://us.i.posthog.com
 *     environment: "production"|"test",
 *     themeRole: <string>,        // main | unpublished | development | demo
 *     template: <string>,         // template.name, e.g. index | product
 *     templateSuffix: <string|null>,
 *     machineHandles: [<string>], // product handles that are "the machine PDP"
 *     product: { handle, type } | null   // only on product templates
 *   }
 *
 * Consent (Shopify Customer Privacy API):
 *   - PostHog is NOT initialized — no script, cookie or storage — until
 *     Shopify.customerPrivacy.analyticsProcessingAllowed() is true.
 *   - `visitorConsentCollected` handles later decisions: a grant initializes
 *     PostHog once (or re-opts-in without a second pageview); a revocation
 *     calls opt_out_capturing(), which stops capture and disables persistence.
 *   - No Customer Privacy API → nothing is collected (fail closed).
 *
 * What is captured:
 *   - exactly one `$pageview` per page load, after consent, with page context
 *     (template, product handle/type, is_machine_pdp) — never customer data;
 *   - `machine_link_click` on the homepage only: destination path, link text
 *     and placement (enclosing Shopify section id) for links to a machine
 *     product page. One delegated listener, bound once, sent by sendBeacon so
 *     navigation does not drop it;
 *   - `$pageleave` (PostHog's built-in duration / bounce signal).
 * Autocapture, session replay, surveys, feature flags, heatmaps, dead clicks,
 * web vitals and person profiles are all off. URL properties keep only
 * utm_* query parameters so tokens such as Klaviyo `_kx`, preview keys or
 * discount codes never leave the browser.
 */
(function () {
  "use strict";

  var root =
    typeof window !== "undefined"
      ? window
      : typeof globalThis !== "undefined"
      ? globalThis
      : this;

  var QUERY_ALLOWLIST = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
  var MACHINE_LINK_EVENT = "machine_link_click";

  // Account and checkout routes can carry bearer tokens or customer IDs in
  // their path, including when they are the referrer for a storefront page.
  function sanitizePath(value) {
    if (typeof value !== "string") return value;
    var sensitive = /\/(account|checkouts?|orders)(?:\/|$)/i.exec(value);
    return sensitive ? "/" + sensitive[1].toLowerCase() : value;
  }

  // Keep scheme/host/path of a URL and only allowlisted query keys; drop the
  // hash. Non-URL strings (e.g. PostHog's "$direct" referrer) are returned
  // with any query string removed.
  function sanitizeUrl(value) {
    if (typeof value !== "string") return value;
    var url;
    try {
      url = new URL(value);
    } catch (e) {
      return sanitizePath(value.split("?")[0].split("#")[0]);
    }
    var kept = [];
    url.searchParams.forEach(function (v, k) {
      if (QUERY_ALLOWLIST.indexOf(k.toLowerCase()) !== -1) {
        kept.push(encodeURIComponent(k) + "=" + encodeURIComponent(v));
      }
    });
    return url.origin + sanitizePath(url.pathname) + (kept.length ? "?" + kept.join("&") : "");
  }

  // Used by posthog-js `before_send`: every property that names a URL or
  // referrer ($current_url, $referrer, $session_entry_url, …) is reduced to its
  // allowlisted form before the event leaves the browser.
  function sanitizeProperties(properties) {
    if (!properties || typeof properties !== "object") return properties;
    for (var key in properties) {
      if (!Object.prototype.hasOwnProperty.call(properties, key)) continue;
      if (/(url|referrer)$/i.test(key) && typeof properties[key] === "string") {
        properties[key] = sanitizeUrl(properties[key]);
      } else if (/pathname$/i.test(key) && typeof properties[key] === "string") {
        properties[key] = sanitizePath(properties[key]);
      }
    }
    return properties;
  }

  function normalizeHandles(list) {
    var out = [];
    (list || []).forEach(function (h) {
      var handle = String(h || "").trim().toLowerCase();
      if (handle) out.push(handle);
    });
    return out;
  }

  // "/products/the-cumulus-machine-1", "/collections/x/products/…", with or
  // without a trailing slash, optionally under a locale prefix.
  function productHandleFromPath(pathname) {
    var match = /\/products\/([^/?#]+)\/?$/.exec(pathname || "");
    return match ? decodeURIComponent(match[1]).toLowerCase() : null;
  }

  function isMachinePath(pathname, handles) {
    var handle = productHandleFromPath(pathname);
    return !!handle && handles.indexOf(handle) !== -1;
  }

  function isMachineProduct(product, handles) {
    return !!(product && product.handle) && handles.indexOf(String(product.handle).toLowerCase()) !== -1;
  }

  // Shopify wraps every section in <div id="shopify-section-{id}">; JSON
  // templates and section groups prefix the id with "template--{n}__" or
  // "sections--{n}__". Return the bare section id (e.g. "frontpage-hero").
  function placementFromSection(section) {
    if (!section || !section.id) return null;
    return String(section.id)
      .replace(/^shopify-section-/, "")
      .replace(/^(template|sections)--\d+__/, "");
  }

  function linkText(anchor) {
    return String(anchor.textContent || "").replace(/\s+/g, " ").trim().slice(0, 80);
  }

  function initOptions(config) {
    return {
      api_host: config.apiHost,
      defaults: "2026-05-30",
      person_profiles: "never",
      opt_out_persistence_by_default: true,
      opt_out_capturing_by_default: true,
      // Send while consent is current instead of leaving a delayed event batch.
      request_batching: false,
      autocapture: false,
      capture_pageview: false, // sent explicitly, once, after consent (see start)
      capture_pageleave: true,
      capture_dead_clicks: false,
      capture_heatmaps: false,
      capture_performance: false,
      disable_session_recording: true,
      disable_surveys: true,
      advanced_disable_flags: true,
      mask_personal_data_properties: true,
      custom_personal_data_properties: ["_kx"],
    };
  }

  // Official loader from https://posthog.com/docs/web-analytics/installation/shopify.
  // It only queues calls; the library itself is fetched when init() runs, which
  // we do after consent.
  function installStub(win, doc) {
    !function(t,e){var o,n,p,r;e.__SV||(win.posthog && win.posthog.__loaded)||(win.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}p||((p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",p.onerror=function(){p=null},(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r));var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],Object.defineProperty(u,"toString",{configurable:!0,enumerable:!0,writable:!0,value:function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e}}),Object.defineProperty(u.people,"toString",{configurable:!0,enumerable:!0,writable:!0,value:function(){return u.toString(1)+".people (stub)"}}),o="init capture register register_once register_for_session unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group identify setPersonProperties setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags resetGroups onFeatureFlags addFeatureFlagsHandler onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(doc,win.posthog||[]);
  }

  /*
   * deps: { posthog: () => posthog client, doc: document-like, win: window-like }
   * Injected so the consent / pageview / click behaviour is unit-testable
   * without a browser.
   */
  function createTracker(config, deps) {
    var handles = normalizeHandles(config.machineHandles);
    var initialized = false;
    var sdkReady = false;
    var consentAllowed = false;
    var pageviewSent = false;
    var clicksBound = false;

    function pageProperties() {
      var props = {
        environment: config.environment,
        theme_role: config.themeRole,
        shopify_template: config.template,
        shopify_template_suffix: config.templateSuffix || null,
        is_homepage: config.template === "index",
        is_machine_pdp: isMachineProduct(config.product, handles),
      };
      if (config.product) {
        props.product_handle = config.product.handle;
        props.product_type = config.product.type || null;
      }
      return props;
    }

    function onClick(event) {
      if (!consentAllowed || !sdkReady) return;
      var target = event && event.target;
      var anchor = target && typeof target.closest === "function" ? target.closest("a[href]") : null;
      if (!anchor) return;
      var url;
      try {
        url = new URL(anchor.getAttribute("href"), deps.win.location.href);
      } catch (e) {
        return;
      }
      if (url.host !== deps.win.location.host || !isMachinePath(url.pathname, handles)) return;
      var props = pageProperties();
      props.link_path = url.pathname;
      props.link_text = linkText(anchor);
      props.placement = placementFromSection(anchor.closest(".shopify-section"));
      deps.posthog().capture(MACHINE_LINK_EVENT, props, { transport: "sendBeacon" });
    }

    function bindClicks() {
      if (clicksBound || config.template !== "index") return;
      clicksBound = true;
      deps.doc.addEventListener("click", onClick, true);
    }

    function capturePage() {
      var ph = deps.posthog();
      // Also clears a stored opt-out from an earlier revocation.
      ph.opt_in_capturing({ captureEventName: false });
      ph.register({ environment: config.environment, theme_role: config.themeRole });
      if (!pageviewSent) {
        pageviewSent = true;
        ph.capture("$pageview", pageProperties());
      }
      bindClicks();
    }

    // Start the SDK after consent, but do not queue opt-in or events while its
    // script is loading: consent may have changed by the time it is ready.
    function start() {
      consentAllowed = true;
      if (!initialized) {
        initialized = true;
        var options = initOptions(config);
        options.before_send = function (event) {
          if (!consentAllowed || !event) return null;
          event.properties = sanitizeProperties(event.properties);
          return event;
        };
        options.loaded = function () {
          sdkReady = true;
          if (consentAllowed) capturePage();
          else deps.posthog().opt_out_capturing({ clear_persistence: true });
        };
        deps.posthog().init(config.token, options);
      } else if (sdkReady) {
        capturePage();
      }
    }

    function stop() {
      consentAllowed = false;
      if (sdkReady) deps.posthog().opt_out_capturing({ clear_persistence: true });
    }

    return { start: start, stop: stop, onClick: onClick, pageProperties: pageProperties };
  }

  // Wire the tracker to Shopify's Customer Privacy API. Listener first, then
  // the initial read, so a decision made while the API loads is not missed.
  function connectConsent(win, doc, tracker) {
    function apply(allowed) {
      if (allowed) tracker.start();
      else tracker.stop();
    }
    doc.addEventListener("visitorConsentCollected", function (event) {
      var detail = event && event.detail;
      if (detail && typeof detail.analyticsAllowed === "boolean") apply(detail.analyticsAllowed);
    });

    var shopify = win.Shopify;
    function readInitial() {
      var api = shopify && shopify.customerPrivacy;
      if (api && typeof api.analyticsProcessingAllowed === "function") {
        apply(api.analyticsProcessingAllowed() === true);
      }
    }
    if (shopify && shopify.customerPrivacy) {
      readInitial();
    } else if (shopify && typeof shopify.loadFeatures === "function") {
      shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], function (error) {
        if (!error) readInitial();
      });
    }
  }

  function boot(win) {
    var config = win.__cumulusPosthogConfig;
    if (!config || !config.token) return;
    installStub(win, win.document);
    var tracker = createTracker(config, {
      win: win,
      doc: win.document,
      posthog: function () {
        return win.posthog;
      },
    });
    connectConsent(win, win.document, tracker);
  }

  // Pure helpers for unit tests; boot only in a real browser document.
  root.__cumulusPosthog = {
    sanitizeUrl: sanitizeUrl,
    sanitizeProperties: sanitizeProperties,
    isMachinePath: isMachinePath,
    placementFromSection: placementFromSection,
    initOptions: initOptions,
    createTracker: createTracker,
    connectConsent: connectConsent,
  };

  if (root && typeof root.document !== "undefined" && root.document) {
    boot(root);
  }
})();
